// Renamed from OAuth2ConsentPage.tsx
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';
import LayoutStandard from '@/components/LayoutStandard';
import LoadingAnimation from '@/components/LoadingAnimation';
import Button, { BUTTON_KIND } from '@/components/Button';
import { submitConsentResponse, OAuth2ConsentRequest } from '@/api/oauth2';

function useOAuth2Params() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  return {
    client_id: params.get('client_id') || '',
    state: params.get('state') || '',
    redirect_uri: params.get('redirect_uri') || '',
    response_type: params.get('response_type') || '',
    scope: params.get('scope') || '',
  };
}

interface OAuth2AuthorizePageProps {
  clientName?: string;
}

const OAuth2AuthorizePage: React.FC<OAuth2AuthorizePageProps> = ({
  clientName = 'Third-Party Application',
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [denied, setDenied] = useState(false);
  const [allowing, setAllowing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { client_id, state, redirect_uri, response_type, scope } = useOAuth2Params();
  const scopes = scope ? scope.split(' ') : [];
  const redirectDomain = redirect_uri ? new URL(redirect_uri).host : '';
  const permissions = [
    'Your organisation (read-only)',
    'Your contacts (read-only)',
  ];


  // Handle Allow
  const handleAllow = async () => {
    setAllowing(true);
    setError(null);
    try {
      const req: OAuth2ConsentRequest = {
        client_id,
        state,
        redirect_uri,
        response_type,
        scope,
      };
      const res = await submitConsentResponse(req);
      window.location.href = `${redirect_uri}?code=${res.code}&state=${state}`;
    } catch (err: any) {
      setAllowing(false);
      setError(err?.response?.data?.error || 'Failed to submit consent.');
    }
  };

  // Handle Deny
  const handleDeny = () => {
    setDenied(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingAnimation className="w-24 h-24 text-brand-600" />
      </div>
    );
  }

  if (!user) {
    // Preserve the full OAuth2 authorize URL (pathname + search)
    const authorizePath = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(authorizePath)}`}
        replace
      />
    );
  }

  if (error) {
    return (
      <LayoutStandard heading="OAuth2 Error">
        <div className="text-red-600 text-center my-8">{error}</div>
      </LayoutStandard>
    );
  }

  if (denied) {
    return (
      <LayoutStandard heading={`Connect Tapt to ${clientName}`}>
        <div className="flex flex-col items-center justify-center min-h-[20vh]">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-700 mb-6 text-center max-w-md">
            You didn't grant access to {clientName}. If this was a mistake, click below to try again.
          </p>
          <Button
            buttonText="Back to Authorize"
            kind={BUTTON_KIND.PRIMARY}
            onClick={() => setDenied(false)}
          />
        </div>
      </LayoutStandard>
    );
  }

  return (
    <LayoutStandard heading={`Connect Tapt to ${clientName}`}>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 border border-gray-100 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            <p className="text-gray-700">
              {clientName} is requesting read-only access to your Tapt account. This allows you to automate workflows using your Tapt data.
            </p>
            <div className="text-gray-700">
              This connection will give {clientName} access to:
              <ul className="list-disc list-inside">
                {permissions.map((permission) => (
                  <li key={permission}>{permission}</li>
                ))}
              </ul>
            </div>
            <p className="text-gray-700">You can revoke access at any time in your Tapt account settings.</p>

            <div className="flex justify-center mt-8 gap-4">
              <Button
                buttonText="Allow"
                kind={BUTTON_KIND.PRIMARY}
                className="flex-1"
                onClick={handleAllow}
                data-testid="consent-allow"
                loading={allowing}
                disabled={allowing}
              />
              <Button
                buttonText="Decline"
                kind={BUTTON_KIND.WHITE}
                className="flex-1"
                onClick={handleDeny}
                data-testid="consent-deny"
                disabled={allowing}
              />
            </div>
            {redirectDomain && (
              <div className="text-xs text-gray-400 text-center mt-6">
                You’ll be redirected to <span className="font-mono">{redirectDomain}</span> after authorising.
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutStandard>
  );
};

export default OAuth2AuthorizePage;
